/**
 * Variation Service
 * Lazy-load variations with caching + TTL and batch support.
 */

import * as StateManager from './state-manager.js';

const CACHE_KEY = 'ktvhvn_variations_cache';
const CACHE_TTL = 60 * 60 * 1000; // 1 hour
const FETCH_TIMEOUT = 5000;
const MAX_RETRIES = 2;

let memoryCache = {};

// Load cache from localStorage once
function loadCache() {
    if (Object.keys(memoryCache).length > 0) {
        return;
    }
    try {
        const raw = localStorage.getItem(CACHE_KEY);
        if (!raw) return;
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
            memoryCache = parsed;
        }
    } catch (e) {
        console.warn('Cannot load variations cache', e);
        memoryCache = {};
    }
}

function saveCache() {
    try {
        localStorage.setItem(CACHE_KEY, JSON.stringify(memoryCache));
    } catch (e) {
        console.warn('Cannot save variations cache', e);
    }
}

function isFresh(entry) {
    if (!entry || !entry.timestamp) return false;
    return (Date.now() - entry.timestamp) < CACHE_TTL;
}

function getCached(productId) {
    loadCache();
    const entry = memoryCache[productId];
    if (!entry || !isFresh(entry)) {
        return null;
    }
    return entry.variations || null;
}

function setCached(productId, variations) {
    if (!Array.isArray(variations)) return;
    memoryCache[productId] = {
        variations: variations,
        timestamp: Date.now()
    };
    saveCache();
}

function mergeIntoCoursesData(productId, variations) {
    const courses = StateManager.getCoursesData();
    const idx = courses.findIndex(c => c.id === productId);
    if (idx !== -1) {
        courses[idx].variations = variations;
        StateManager.setCoursesData(courses);
    }
}

function ajaxWithTimeout(params) {
    return new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
            reject(new Error('Timeout'));
        }, FETCH_TIMEOUT);

        jQuery.ajax(params).done(res => {
            clearTimeout(timer);
            resolve(res);
        }).fail((xhr, status, error) => {
            clearTimeout(timer);
            reject(new Error(error || status || 'AJAX error'));
        });
    });
}

async function fetchBatch(productIds) {
    const payload = {
        action: 'ktvhvn_get_product_variations_batch',
        nonce: window.ktvhvnCourseReg?.nonce || '',
        product_ids: productIds
    };

    let attempt = 0;
    while (attempt <= MAX_RETRIES) {
        try {
            const response = await ajaxWithTimeout({
                url: window.ktvhvnCourseReg?.ajax_url || '/wp-admin/admin-ajax.php',
                method: 'POST',
                data: payload
            });

            if (response && response.success) {
                return response.data;
            }
            attempt++;
        } catch (e) {
            attempt++;
            if (attempt > MAX_RETRIES) {
                throw e;
            }
        }
    }
    throw new Error('Failed to fetch variations');
}

/**
 * Ensure variations for multiple products.
 * Returns map { productId: variations }
 */
export async function ensureBatch(productIds = []) {
    const result = {};
    const missing = [];

    productIds.forEach(pid => {
        const cached = getCached(pid);
        if (cached) {
            result[pid] = cached;
        } else {
            missing.push(pid);
        }
    });

    if (missing.length > 0) {
        const fetched = await fetchBatch(missing);
        Object.entries(fetched).forEach(([pid, variations]) => {
            const productId = parseInt(pid, 10);
            if (Array.isArray(variations) && variations.length > 0) {
                setCached(productId, variations);
                mergeIntoCoursesData(productId, variations);
                result[productId] = variations;
            }
        });
    }

    return result;
}

/**
 * Ensure variations for a single product, returns array.
 */
export async function ensureVariations(productId) {
    if (!productId) return [];

    const cached = getCached(productId);
    if (cached) {
        return cached;
    }

    const res = await ensureBatch([productId]);
    return res[productId] || [];
}
